
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSite } from "./siteContext";
import { useAuth } from "./authContext";
import { getUserCart, updateUserCart } from "../services/cartService";

export const CartContext = createContext();

// Maximum distinct parts a quote may be SUBMITTED with. Parts can always be added to the cart;
// this only gates RFQ/order placement, where the server enforces the same number.
export const MAX_CART_LINES = 4000;

// Helper function to get product ID, handling both 'ProductID' and 'product_id'
const getNormalizedProductId = (item) =>
  item?.ProductID ?? item?.product_id ?? item?.productId ?? item?.id;
const toProductIdKey = (value) => String(value ?? "");
const normalizeScopeValue = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized && normalized !== "undefined" && normalized !== "null" ? normalized : "";
};
const getScopeKey = (item = {}) => {
  const siteId = normalizeScopeValue(item?.site_id ?? item?.siteId);
  const cusId = normalizeScopeValue(item?.cus_id ?? item?.customer_id ?? item?.customerId);
  return siteId && cusId ? `${siteId}:${cusId}` : "";
};
const getScopeIdsFromSelected = (selectedSiteData = {}, selectedSiteKey = "") => {
  const siteIdFromData = normalizeScopeValue(selectedSiteData?.site_id);
  const cusIdFromData = normalizeScopeValue(
    selectedSiteData?.cus_id ?? selectedSiteData?.customer_id
  );
  if (siteIdFromData && cusIdFromData) {
    return { site_id: siteIdFromData, cus_id: cusIdFromData };
  }
  const key = String(selectedSiteKey || "").trim();
  if (!key || !key.includes(":")) return {};
  const [siteIdFromKey, cusIdFromKey] = key.split(":");
  const site_id = normalizeScopeValue(siteIdFromKey);
  const cus_id = normalizeScopeValue(cusIdFromKey);
  return site_id && cus_id ? { site_id, cus_id } : {};
};

export const getCartItemKey = (item = {}) => {
  const productId = toProductIdKey(getNormalizedProductId(item));
  const site = String(item?.Site ?? "");
  const scopeKey = getScopeKey(item) || `site:${site}`;
  const machineId = normalizeScopeValue(item?.machine_id ?? item?.machineId);
  const siteLineId = normalizeScopeValue(item?.site_line_id ?? item?.siteLineId);
  return [productId, scopeKey, `machine:${machineId}`, `line:${siteLineId}`].join("|");
};
const mergeCartItemsByKey = (items = []) => {
  const merged = new Map();
  (items || []).forEach((item) => {
    if (!item) return;
    const key = getCartItemKey(item);
    const qty = parseInt(item?.Quantity, 10) || 0;
    const existing = merged.get(key);
    if (existing) {
      existing.Quantity = (parseInt(existing.Quantity, 10) || 0) + qty;
    } else {
      merged.set(key, { ...item, Quantity: qty || 1 });
    }
  });
  return Array.from(merged.values());
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const cartItemsRef = useRef([]);
  const [userId, setUserId] = useState(null);
  const { selectedSite, selectedSiteData, selectedSiteKey } = useSite();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const debounceTimer = useRef(null);

  useEffect(() => {
    cartItemsRef.current = cartItems;
  }, [cartItems]);

  const resolvedSelectedScopeKey = useMemo(() => {
    const explicit = String(selectedSiteKey || "").trim();
    if (explicit) return explicit;
    const siteId = normalizeScopeValue(selectedSiteData?.site_id);
    const cusId = normalizeScopeValue(selectedSiteData?.cus_id);
    return siteId && cusId ? `${siteId}:${cusId}` : "";
  }, [selectedSiteKey, selectedSiteData?.site_id, selectedSiteData?.cus_id]);

  const isItemInSelectedScope = useCallback(
    (item = {}) => {
      const itemScopeKey = getScopeKey(item);
      if (resolvedSelectedScopeKey && itemScopeKey) {
        return itemScopeKey === resolvedSelectedScopeKey;
      }
      if (resolvedSelectedScopeKey && !itemScopeKey) {
        return false;
      }
      return String(item?.Site ?? "") === String(selectedSite ?? "");
    },
    [selectedSite, resolvedSelectedScopeKey]
  );

  const cartCount = useMemo(() => {
    if (!cartItems || cartItems.length === 0) {
      return 0;
    }
    const filteredItems = cartItems.filter(
      (item) => isItemInSelectedScope(item)
    );
    return filteredItems.reduce(
      (total, item) => total + (parseInt(item.Quantity) || 0),
      0
    );
  }, [cartItems, isItemInSelectedScope]);

  const pendingSaveRef = useRef(null);
  const inFlightSaveRef = useRef(null);
  const fetchSeqRef = useRef(0);

  const writeCartToServer = useCallback(async (items, targetUserId) => {
    if (!targetUserId) return;
    const promise = (async () => {
      try {
        await updateUserCart(targetUserId, items);
      } catch (error) {
        console.error("Error updating cart on the server:", error);
        throw error;
      }
    })();
    inFlightSaveRef.current = promise;
    try {
      await promise;
    } finally {
      if (inFlightSaveRef.current === promise) inFlightSaveRef.current = null;
    }
  }, []);

  const flushPendingCartSave = useCallback(async () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    const items = pendingSaveRef.current;
    if (items) {
      pendingSaveRef.current = null;
      try {
        await writeCartToServer(items, userId);
      } catch {
        // surfaced by writeCartToServer; local state stays authoritative
      }
      return;
    }
    if (inFlightSaveRef.current) {
      try {
        await inFlightSaveRef.current;
      } catch {
        // already logged
      }
    }
  }, [userId, writeCartToServer]);

  const fetchCart = async (userId) => {
    if (!userId) return;
    const seq = fetchSeqRef.current + 1;
    fetchSeqRef.current = seq;
    setIsLoading(true);
    try {
      const response = await getUserCart(userId);
      // Ignore this response if a newer fetch started, or if a local edit is waiting to be
      // written -- the server copy is older than what the user is currently looking at.
      if (seq !== fetchSeqRef.current) return;
      if (pendingSaveRef.current || inFlightSaveRef.current) return;
      const serverCart = Array.isArray(response?.data) ? response.data : [];
      setCartItems(mergeCartItemsByKey(serverCart));
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  useEffect(() => {
    if (user) {
      setUserId(user.user_id);
    }
  }, [user]);

  useEffect(() => {
    if (userId) {
      fetchCart(userId);
    }
  }, [userId]);

  const refreshCart = useCallback(async () => {
    if (!userId) return;
    // Make sure any debounced local edit has reached the server before reading it back,
    // otherwise the GET returns pre-edit data and clobbers what the user just did.
    await flushPendingCartSave();
    await fetchCart(userId);
  }, [userId, flushPendingCartSave]);

  const updateCart = async (newCartItems) => {
    const normalizedCartItems = mergeCartItemsByKey(newCartItems);

    cartItemsRef.current = normalizedCartItems;
    setCartItems(normalizedCartItems); // Optimistic UI update
    pendingSaveRef.current = normalizedCartItems;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;
      const items = pendingSaveRef.current;
      if (!items || !userId) return;
      pendingSaveRef.current = null;
      writeCartToServer(items, userId).catch(() => {
        // Write failed: fall back to whatever the server has so the UI stops lying.
        fetchCart(userId);
      });
    }, 500); // 500ms debounce delay

    return { ok: true, count: normalizedCartItems.length, limit: MAX_CART_LINES };
  };

  const addToCart = async (product) => {
    const currentCartItems = Array.isArray(cartItemsRef.current) ? cartItemsRef.current : [];
    const scopeIds = getScopeIdsFromSelected(selectedSiteData, selectedSiteKey);
    const productToAdd = {
      ...product,
      Quantity: parseInt(product.Quantity) || 1,
      ...scopeIds,
    };

    const incomingCartKey = getCartItemKey(productToAdd);
    const existingProductIndex = currentCartItems.findIndex(
      (item) => getCartItemKey(item) === incomingCartKey
    );

    let newCartItems;
    if (existingProductIndex > -1) {
      newCartItems = currentCartItems.map((item, index) =>
        index === existingProductIndex
          ? {
              ...item,
              Quantity:
                parseInt(item.Quantity) + parseInt(productToAdd.Quantity),
            }
          : item
      );
    } else {
      newCartItems = [...currentCartItems, productToAdd];
    }
    return updateCart(newCartItems);
  };

  const addMultipleToCart = async (products) => {
    let newCartItems = [...(Array.isArray(cartItemsRef.current) ? cartItemsRef.current : [])];
    const scopeIds = getScopeIdsFromSelected(selectedSiteData, selectedSiteKey);
    products.forEach((product) => {
      const productQuantity = Number(product.Quantity ?? 1) || 1;
      const scopedProduct = {
        ...product,
        ...scopeIds,
      };
      const incomingCartKey = getCartItemKey(scopedProduct);
      const existingProductIndex = newCartItems.findIndex(
        (item) => getCartItemKey(item) === incomingCartKey
      );

      if (existingProductIndex > -1) {
        newCartItems[existingProductIndex].Quantity =
          (Number(newCartItems[existingProductIndex].Quantity) || 0) + productQuantity;
      } else {
        newCartItems.push({
          ...product,
          Quantity: productQuantity,
          ...scopeIds,
        });
      }
    });
    return updateCart(newCartItems);
  };

  const removeFromCart = async (productId, cartKey = "") => {
    const cartItems = Array.isArray(cartItemsRef.current) ? cartItemsRef.current : [];
    const targetKey = toProductIdKey(productId);
    const targetCartKey = String(cartKey || "").trim();
    let removedByExactKey = false;
    const byKeyFiltered = cartItems.filter((item) => {
      if (!targetCartKey) return true;
      const keep = getCartItemKey(item) !== targetCartKey;
      if (!keep) removedByExactKey = true;
      return keep;
    });
    const newCartItems = targetCartKey && !removedByExactKey
      ? cartItems.filter(
          (item) =>
            !(toProductIdKey(getNormalizedProductId(item)) === targetKey && isItemInSelectedScope(item))
        )
      : (targetCartKey ? byKeyFiltered : cartItems.filter(
          (item) =>
            !(toProductIdKey(getNormalizedProductId(item)) === targetKey && isItemInSelectedScope(item))
        ));
    await updateCart(newCartItems);
  };

  const clearCart = async () => {
    const cartItems = Array.isArray(cartItemsRef.current) ? cartItemsRef.current : [];
    const newCartItems = cartItems.filter(
      (item) => !isItemInSelectedScope(item)
    );
    await updateCart(newCartItems);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartCount,
        addToCart,
        removeFromCart,
        clearCart,
        updateCart,
        addMultipleToCart,
        isLoading,
        isInitialized,
        refreshCart,
        maxCartLines: MAX_CART_LINES,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
